import PropTypes from 'prop-types';
import { memo, useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import EditorToolbar from '../../../../components/shared/EditorToolbar';
import AddSnippetModal from '../modals/AddSnippetModal';

const MessageInput = memo(
  ({
    value = '',
    onChange,
    onSend,
    onAIAssist,
    onAddNote,
    placeholder = 'Type your message here',
    disabled = false,
    isLoading = false,
    conversationId,
    wabaId,
    accessToken,
    conversationStatus = 'active',
    isWindowExpired = false,
  }) => {
    const textInputRef = useRef(null);
    const [inputMode, setInputMode] = useState('normal'); // 'normal', 'ai-assist', 'note'
    const [showAIPopup, setShowAIPopup] = useState(false);
    const [selectedTone, setSelectedTone] = useState('Professional');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [showSnippetModal, setShowSnippetModal] = useState(false);

    // Get current user info from Redux
    const currentUser = useSelector((state) => state.auth.user);

    // Focus input when mode changes
    useEffect(() => {
      if (textInputRef.current) {
        textInputRef.current.focus();
      }
    }, []);
    const handleSendClick = async () => {
      if (
        value.trim() &&
        !disabled &&
        !isLoading &&
        !isSubmitting &&
        !(conversationStatus === 'closed' && isWindowExpired)
      ) {
        setIsSubmitting(true);
        const messageData = {
          text: value.trim(),
          agent: {
            user_id: currentUser.id,
            email: currentUser.email,
            name: currentUser.name,
          },
        };

        try {
          if (inputMode === 'ai-assist' && onAIAssist) {
            await onAIAssist({ ...messageData, tone: selectedTone });
            // Don't clear input - let the generated text stay for user to review and send
          } else if (inputMode === 'note' && onAddNote) {
            await onAddNote({ noteText: messageData.text, agent: messageData.agent });
            // Clear input and reset mode after note is saved
            if (onChange) {
              onChange({ target: { value: '' } });
            }
          } else if (onSend) {
            onSend(value.trim());
            // Don't clear input here - let parent component handle it
          }

          // Reset to normal mode after sending (except for AI assist - let user review generated text)
          if (inputMode === 'note') {
            setInputMode('normal');
            setShowAIPopup(false);
          } else if (inputMode === 'ai-assist') {
            // Reset mode but keep the generated text for user to review
            setInputMode('normal');
            setShowAIPopup(false);
          }
        } catch (error) {
          console.error('Error processing message:', error);
        } finally {
          setIsSubmitting(false);
        }
      }
    };

    const handleKeyPressInternal = (e) => {
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        handleSendClick();
      }
    };

    const isButtonEnabled =
      value.trim() && !disabled && !isLoading && !isSubmitting && !(conversationStatus === 'closed' && isWindowExpired);

    // Dynamic styling based on input mode
    const getInputContainerStyle = () => {
      const baseStyle = {
        display: 'flex',
        flexDirection: 'row',
        gap: '8px',
        padding: '10px 14px',
        border: '1px solid #D0D5DD',
        borderRadius: '4px',
        boxShadow: '0px 1px 2px rgba(16, 24, 40, 0.05)',
        flex: '1',
        alignItems: 'center',
      };

      switch (inputMode) {
        case 'ai-assist':
          return {
            ...baseStyle,
            backgroundColor: '#EDE7F6',
            border: '1px solid #ACA9F1',
          };
        case 'note':
          return {
            ...baseStyle,
            backgroundColor: '#FFF3E0',
            border: '1px solid #FF9800',
          };
        default:
          return {
            ...baseStyle,
            backgroundColor: '#FFFFFF',
          };
      }
    };

    const getPlaceholderText = () => {
      switch (inputMode) {
        case 'ai-assist':
          return 'Refine your message with the right tone—just ask AI to help.';
        case 'note':
          return 'Type your note here.. *This note will only visible to your team';
        default:
          return placeholder;
      }
    };

    const getSendButtonStyle = () => {
      const baseStyle = {
        display: 'flex',
        flexDirection: 'row',
        justifyContent: 'center',
        alignItems: 'center',
        gap: '8px',
        padding: '10px 18px',
        border: 'none',
        borderRadius: '4px',
        boxShadow: '0px 1px 2px rgba(16, 24, 40, 0.05)',
        cursor: isButtonEnabled ? 'pointer' : 'not-allowed',
        transition: 'all 0.2s ease',
        alignSelf: 'stretch',
      };

      if (!isButtonEnabled) {
        return {
          ...baseStyle,
          backgroundColor: '#F2F4F7',
        };
      }

      switch (inputMode) {
        case 'ai-assist':
          return {
            ...baseStyle,
            backgroundColor: '#673AB7',
          };
        case 'note':
          return {
            ...baseStyle,
            backgroundColor: '#FF9800',
          };
        default:
          return {
            ...baseStyle,
            backgroundColor: '#0BA9E0',
          };
      }
    };

    // Placeholder handlers for toolbar functionality
    const handleTextChange = (newValue) => {
      if (onChange) {
        onChange({ target: { value: newValue } });
      }
    };

    const handleAddSnippet = () => {
      setShowSnippetModal(true);
    };

    const handleAddNoteClick = () => {
      setInputMode('note');
      setShowAIPopup(false);
    };

    const handleAIAssistClick = () => {
      setShowAIPopup(!showAIPopup);
      if (!showAIPopup) {
        setInputMode('normal'); // Reset mode when closing popup
      }
    };

    const handleToneSelect = (tone) => {
      setSelectedTone(tone);
      setInputMode('ai-assist');
      setShowAIPopup(false);
    };

    const handleCancelMode = () => {
      setInputMode('normal');
      setShowAIPopup(false);
    };

    const handleInsertSnippet = (snippetContent) => {
      if (onChange) {
        // Insert snippet content at cursor position or replace current value
        const currentValue = value || '';
        const newValue = currentValue + (currentValue ? ' ' : '') + snippetContent;
        onChange({ target: { value: newValue } });
      }
      setShowSnippetModal(false);
    };

    const handleCloseSnippetModal = () => {
      setShowSnippetModal(false);
    };

    return (
      <div
        style={{
          padding: '15px 30px 20px 20px',
          backgroundColor: '#FFFFFF',
          borderTop: '1px solid #CFD8DC',
          borderRadius: '0px 0px 20px 0px',
          display: 'flex',
          flexDirection: 'column',
          alignSelf: 'stretch',
          gap: '10px',
          position: 'relative',
        }}
      >
        {/* Closed Conversation Banner - Only show when window is expired */}
        {conversationStatus === 'closed' && isWindowExpired && (
          <div
            style={{
              backgroundColor: '#FDF2F2',
              border: '1px solid #FECACA',
              borderRadius: '8px',
              padding: '12px 16px',
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
              marginBottom: '10px',
            }}
          >
            <div
              style={{
                width: '20px',
                height: '20px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" role="img" aria-labelledby="warningIconTitle">
                <title id="warningIconTitle">Warning</title>
                <circle cx="12" cy="12" r="10" fill="#F59E0B" />
                <path
                  d="M12 8v4m0 4h.01"
                  stroke="white"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </div>
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                gap: '4px',
              }}
            >
              <span
                style={{
                  fontFamily: 'Poppins',
                  fontWeight: '600',
                  fontSize: '14px',
                  lineHeight: '1.5em',
                  color: '#DC2626',
                }}
              >
                WhatsApp customer service window closed
              </span>
              <span
                style={{
                  fontFamily: 'Poppins',
                  fontWeight: '400',
                  fontSize: '12px',
                  lineHeight: '1.5em',
                  color: '#7F1D1D',
                }}
              >
                You can&apos;t send messages because it&apos;s been more than 24 hours since the contact&apos;s
                last reply, as per WhatsApp&apos;s policy.
              </span>
            </div>
          </div>
        )}
        {/* AI Assist Popup */}
        {showAIPopup && (
          <div
            style={{
              position: 'absolute',
              bottom: '100%',
              right: '20px',
              marginBottom: '10px',
              backgroundColor: '#FFFFFF',
              borderRadius: '16px',
              boxShadow:
                '0px 3.75px 11px 0px rgba(0, 0, 0, 0.19), 0px 0.5px 5px 0px rgba(0, 0, 0, 0.04)',
              zIndex: 1000,
              overflow: 'hidden',
            }}
          >
            {/* Header */}
            <div
              style={{
                padding: '10px 16px',
                backgroundColor: '#FFFFFF',
                borderRadius: '4px 4px 0px 0px',
                borderBottom: '1px solid #F2F4F7',
              }}
            >
              <span
                style={{
                  fontFamily: 'Poppins',
                  fontWeight: '400',
                  fontSize: '12px',
                  lineHeight: '1.5em',
                  color: '#9CA8BB',
                }}
              >
                Select tone
              </span>
            </div>

            {/* Tone Options */}
            {['Professional', 'Concise', 'Friendly', 'Simple'].map((tone, index) => (
              <button
              type="button"
                key={tone}
                onClick={() => handleToneSelect(tone)}
                style={{
                  padding: '10px 16px',
                  backgroundColor: selectedTone === tone ? '#EDE7F6' : '#FFFFFF',
                  cursor: 'pointer',
                  borderRadius: index === 3 ? '0px 0px 4px 4px' : '0px',
                  transition: 'background-color 0.2s ease',
                  width: '150px',
                  border: 'none',
                  textAlign: 'left',
                }}
                onMouseEnter={(e) => {
                  if (selectedTone !== tone) {
                    e.target.style.backgroundColor = '#F8F9FA';
                  }
                }}
                onMouseLeave={(e) => {
                  if (selectedTone !== tone) {
                    e.target.style.backgroundColor = '#FFFFFF';
                  }
                }}
              >
                <span
                  style={{
                    fontFamily: 'Poppins',
                    fontWeight: '500',
                    fontSize: '14px',
                    lineHeight: '1.5em',
                    letterSpacing: '0.25%',
                    color: selectedTone === tone ? '#673AB7' : '#344054',
                  }}
                >
                  {tone}
                </span>
              </button>
            ))}
          </div>
        )}

        {/* Main input area */}
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignSelf: 'stretch',
            gap: '6px',
          }}
        >
          <div
            style={{
              display: 'flex',
              flexDirection: 'row',
              alignSelf: 'stretch',
              gap: '6px',
            }}
          >
            {/* Input field container */}
            <div style={getInputContainerStyle()}>
              {/* Text input */}
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'row',
                  justifyContent: 'stretch',
                  alignItems: 'stretch',
                  gap: '8px',
                  flex: '1',
                }}
              >
                <input
                  ref={textInputRef}
                  type="text"
                  value={value}
                  onChange={onChange}
                  onKeyPress={handleKeyPressInternal}
                  placeholder={getPlaceholderText()}
                  disabled={disabled || isLoading || (conversationStatus === 'closed' && isWindowExpired)}
                  style={{
                    flex: '1',
                    border: 'none',
                    outline: 'none',
                    fontFamily: 'Poppins',
                    fontWeight: '400',
                    fontSize: '13px',
                    lineHeight: '1.5em',
                    color: disabled ? '#BDBDBD' : inputMode === 'note' ? '#737C8A' : '#101828',
                    backgroundColor: 'transparent',
                    '::placeholder': {
                      color: '#737C8A',
                    },
                  }}
                />
              </div>

              {/* AI Assist button - only show in normal mode */}
              {inputMode === 'normal' && (
                <button
                  type="button"
                  onClick={handleAIAssistClick}
                  style={{
                    display: 'flex',
                    flexDirection: 'row',
                    alignItems: 'center',
                    gap: '5px',
                    cursor: 'pointer',
                    padding: '2px',
                    border: 'none',
                    backgroundColor: 'transparent',
                  }}
                >
                  <div
                    style={{
                      width: '17px',
                      height: '17px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <svg
                      width="17"
                      height="18"
                      viewBox="0 0 17 18"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                      role="img"
                      aria-labelledby="aiAssistIconTitle"
                    >
                      <title id="aiAssistIconTitle">AI Assist</title>
                      <path
                        d="M13.6708 6.8219L10.6604 3.8469L11.6521 2.85523C11.9236 2.5837 12.2571 2.44794 12.6526 2.44794C13.0481 2.44794 13.3816 2.5837 13.6531 2.85523L14.6448 3.8469C14.9163 4.11842 15.058 4.44603 15.0698 4.82971C15.0816 5.21339 14.9517 5.54099 14.6802 5.81252L13.6708 6.8219ZM12.6438 7.86669L5.13542 15.375H2.125V12.3646L9.63333 4.85627L12.6438 7.86669Z"
                        fill={inputMode === 'ai-assist' ? '#673AB7' : '#0BA9E0'}
                      />
                    </svg>
                  </div>
                  <span
                    style={{
                      fontFamily: 'Poppins',
                      fontWeight: '400',
                      fontSize: '13px',
                      lineHeight: '1.5em',
                      color: inputMode === 'ai-assist' ? '#673AB7' : '#737C8A',
                    }}
                  >
                    AI Assist
                  </span>
                </button>
              )}
            </div>

            {/* Send button */}
            <button
              type="button"
              onClick={(e) => {
                e.preventDefault();
                handleSendClick();
              }}
              disabled={!isButtonEnabled}
              style={getSendButtonStyle()}
            >
              <div
                style={{
                  width: '17px',
                  height: '17px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <svg width="13" height="11" viewBox="0 0 24 24" fill="none" role="img" aria-labelledby="sendIconTitle">
                  <title id="sendIconTitle">Send</title>
                  <path
                    d="M2.01 21L23 12L2.01 3L2 10L17 12L2 14L2.01 21Z"
                    fill={isButtonEnabled ? '#FFFFFF' : '#98A2B3'}
                  />
                </svg>
              </div>
              <span
                style={{
                  fontFamily: 'Poppins',
                  fontWeight: '500',
                  fontSize: '13px',
                  lineHeight: '1.5em',
                  color: isButtonEnabled ? '#FFFFFF' : '#98A2B3',
                }}
              >
                Send
              </span>
            </button>
          </div>

          {/* Formatting toolbar - only show in normal mode */}
          {inputMode === 'normal' && (
            <EditorToolbar
              textValue={value}
              textInputRef={textInputRef}
              onTextChange={handleTextChange}
              variant="message"
              showEmojiPicker={true}
              showAddSnippet={true}
              showAddNote={true}
              onAddSnippet={handleAddSnippet}
              onAddNote={handleAddNoteClick}
              disabled={conversationStatus !== 'active'}
            />
          )}
        </div>

        {/* Add Snippet Modal */}
        <AddSnippetModal
          show={showSnippetModal}
          onHide={handleCloseSnippetModal}
          onInsertSnippet={handleInsertSnippet}
          wabaId={wabaId}
          accessToken={accessToken}
        />
      </div>
    );
  }
);

MessageInput.propTypes = {
  value: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  onKeyPress: PropTypes.func,
  onSend: PropTypes.func.isRequired,
  onAIAssist: PropTypes.func,
  onAddNote: PropTypes.func,
  placeholder: PropTypes.string,
  disabled: PropTypes.bool,
  isLoading: PropTypes.bool,
  conversationId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  wabaId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  accessToken: PropTypes.string,
  conversationStatus: PropTypes.oneOf(['active', 'closed']),
  isWindowExpired: PropTypes.bool,
};

MessageInput.displayName = 'MessageInput';

export default MessageInput;
